"use client";

import React, { useState, useEffect, useRef } from "react";
import { Menu, Search, User, X, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { ProductsSearch, useWPQueryForSearch } from "@/components/shop/ProductsSearch";
import { CartIcon } from "@/components/shop/CartIcon";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { cn } from "@/lib/utils";
function SearchResultsConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div className="flex items-center justify-center py-4 text-muted-foreground">
        <Loader className="size-4 animate-spin mr-2" />
        <span className="text-xs">Loading...</span>
      </div>;
  }
  if (error) {
    return <p className="py-2 text-xs text-destructive">Failed to load products.</p>;
  }
  if (!products.length) {
    return <p className="py-2 text-xs text-muted-foreground">No matching products found.</p>;
  }
  return <div className="grid gap-2">
      {products.slice(0, 5).map((product, i) => <Link key={product.id} to={product.link} className="flex items-center gap-3 rounded-md p-2 hover:bg-muted transition-colors group" data-index={i}>
          {product.featured_image_url ? <div className="h-10 w-10 shrink-0 overflow-hidden rounded-sm bg-muted">
              <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover" />
            </div> : <div className="h-10 w-10 shrink-0 rounded-sm bg-muted flex items-center justify-center">
              <Search className="size-4 text-muted-foreground/50" />
            </div>}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-foreground group-hover:text-secondary transition-colors">
              {product.title}
            </p>
            {product.price !== undefined && <p className="text-xs text-muted-foreground">
                ${product.price}
              </p>}
          </div>
        </Link>)}
    </div>;
}
function SearchDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [wp_query, hasSearchQuery] = useWPQueryForSearch(searchValue);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  return <div className="relative" ref={containerRef}>
      <Button variant="ghost" size="icon" onClick={() => setIsOpen(!isOpen)} className="text-foreground border border-secondary/30 hover:bg-black/40 hover:text-secondary transition-all duration-300" aria-label="Search">
        <Search className="size-5" />
      </Button>

      {isOpen && <div className="absolute right-0 top-full mt-2 w-[320px] sm:w-[400px] rounded-md border border-secondary/20 bg-[#121212] p-4 shadow-xl z-50 animate-in fade-in zoom-in-95 duration-200">
          <EcommerceProductsProvider wp_query={wp_query} postType="product">
            <div className="space-y-4">
              <div className="relative">
                <ProductsSearch value={searchValue} onChange={setSearchValue} className="w-full" placeholder="Search antiques..." />
              </div>
              <div className="max-h-[60vh] overflow-y-auto">
                {!hasSearchQuery ? <p className="text-xs text-muted-foreground px-1">
                    Type to search our collection...
                  </p> : <SearchResultsConsumer />}
              </div>
            </div>
          </EcommerceProductsProvider>
        </div>}
    </div>;
}
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-8">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-20 bg-muted animate-pulse rounded" data-index={i} />)}
      </div>;
  }
  return <NavigationMenu>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative block py-2 font-sans text-sm font-medium uppercase tracking-[1.5px] text-foreground transition-colors hover:text-secondary">
                {item.label}
                <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-secondary transition-all duration-300 ease-out group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-4 p-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-6 w-32 bg-muted animate-pulse rounded" data-index={i} />)}
      </div>;
  }
  return <nav className="flex flex-col space-y-6 p-6">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="font-serif text-2xl font-medium text-foreground hover:text-secondary transition-colors" onClick={onClose} data-index={i}>
          {item.label}
        </Link>)}
    </nav>;
}
export default function MainNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <header id="main-navigation" className={cn("sticky top-0 z-50 w-full border-b border-transparent transition-all duration-300", isScrolled ? "bg-[#121212]/90 backdrop-blur-lg border-secondary/20 shadow-lg" : "bg-[#121212] border-transparent")}>
      

      {}
      {isMobileMenuOpen && <div className="fixed inset-0 z-50 flex flex-col bg-[#121212] lg:hidden animate-in slide-in-from-right duration-300">
          <div className="flex h-20 items-center justify-between px-4 sm:px-8 border-b border-secondary/10">
            <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1569214/img/image_yywata7m.png" alt="NOBILITIES ANTIQUES GENERAL TRADING - L.L.C." className="h-7 w-auto object-contain" />
              <span className="font-serif text-[10px] font-bold tracking-[0.15em] text-foreground uppercase max-w-[150px] leading-tight">
                NOBILITIES ANTIQUES GENERAL TRADING - L.L.C.
              </span>
            </Link>
            <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(false)} aria-label="Close Menu">
              <X className="size-6" />
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto">
            <MenuProvider menu_id="22" className="w-full">
              <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
            <div className="border-t border-secondary/10 p-6">
              <Link to="/my-account" className="flex items-center gap-3 text-lg font-medium text-foreground hover:text-secondary" onClick={() => setIsMobileMenuOpen(false)}>
                <User className="size-5" />
                My Account
              </Link>
            </div>
          </div>
        </div>}
    </header>;
}